import { useEffect, useState } from 'react';

import {
  collect,
  hasErrors,
  needsAlert,
  serverFieldErrors,
  type ClientErrors,
  type CodeFields,
} from '../forms.ts';
import { useLang } from '../i18n.ts';
import type { LocalizedText } from '@dob/domain';

function without(errors: ClientErrors, field: string): ClientErrors {
  return Object.fromEntries(Object.entries(errors).filter(([key]) => key !== field));
}

/**
 * Brings the first field with an error into view and puts the cursor in it. Only one form is
 * active at a time: the one in the open dialog, otherwise the one on the page.
 */
export function focusFirstInvalid(): void {
  const field =
    document.querySelector<HTMLElement>('dialog[open] [aria-invalid="true"]') ??
    document.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (!field) return;
  field.scrollIntoView({ block: 'center', behavior: 'smooth' });
  field.focus({ preventScroll: true });
}

/**
 * Per-field errors of one form (D93, made more visible after the owner's testing — D97): client
 * checks run on submit, server refusals land under the field they name, and editing a field clears
 * its message. After a refused submit the first field with an error is focused and `summary` lists
 * the names of the fields with an error, for the short alert at the top of the form. `alertError`
 * is what is left for the alert (nothing, when every problem is already shown under a field).
 *
 * `fields` maps each field of the form to its label, in the current language.
 */
export function useFormErrors(
  serverError: unknown,
  options: {
    readonly fields: Readonly<Record<string, string>>;
    readonly codeFields?: CodeFields;
  },
) {
  const { lang } = useLang();
  const [client, setClient] = useState<ClientErrors>({});
  const [submitted, setSubmitted] = useState(false);
  // Fields edited since the server answered: their server message no longer applies.
  const [edited, setEdited] = useState<{ error: unknown; fields: ReadonlySet<string> }>({
    error: null,
    fields: new Set(),
  });
  const cleared = edited.error === serverError ? edited.fields : new Set<string>();
  const server = serverFieldErrors(serverError, options.codeFields);
  const names = Object.keys(options.fields);

  const messageFor = (field: string): LocalizedText | undefined =>
    client[field] ?? (cleared.has(field) ? undefined : server[field]);

  // A refusal from the server that names fields: show the first one, like a refused client check.
  const serverHasFields = Object.keys(server).some((field) => names.includes(field));
  useEffect(() => {
    if (serverHasFields) focusFirstInvalid();
  }, [serverError, serverHasFields]);

  const invalid = names.filter((field) => messageFor(field) !== undefined);

  return {
    /** The message to show under `field`, in the current language. */
    errorFor(field: string): string | undefined {
      return messageFor(field)?.[lang];
    },
    /** Runs the checks; returns true when the form may be sent. */
    validate(checks: Record<string, LocalizedText | undefined>): boolean {
      const errors = collect(checks);
      setClient(errors);
      setSubmitted(true);
      if (!hasErrors(errors)) return true;
      // After React has shown the messages (and marked the fields invalid).
      requestAnimationFrame(() => {
        focusFirstInvalid();
      });
      return false;
    },
    /** Checks one field (e.g. when leaving it) without touching the others. */
    check(field: string, message: LocalizedText | undefined): void {
      setClient((current) => ({
        ...without(current, field),
        ...(message ? { [field]: message } : {}),
      }));
    },
    /** Call from each field's onChange. */
    touched(field: string): void {
      setClient((current) => (field in current ? without(current, field) : current));
      setEdited((current) => ({
        error: serverError,
        fields: new Set([...(current.error === serverError ? current.fields : []), field]),
      }));
    },
    /** Labels of the fields with an error (each once), once the form was sent; empty otherwise. */
    summary:
      submitted || serverHasFields
        ? [...new Set(invalid.map((field) => options.fields[field] ?? field))]
        : [],
    /** Field paths whose reasons are shown under the fields (not repeated in the alert). */
    fields: names,
    alertError: needsAlert(serverError, names, options.codeFields) ? serverError : null,
  };
}

export type FormErrors = ReturnType<typeof useFormErrors>;
